import { useCallback, useEffect, useReducer, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { WmButton } from "@watermarkinsights/ripple-react";
import { buildSvgIconHref } from "../ai-chat-message-utils";
import { formatEmailText, type DraftMessageOutput } from "./draftMessageService";
import "./AiDraftMessage.scss";

interface DraftResponseViewProps {
  output: DraftMessageOutput;
  showAdjustmentHint?: boolean;
}

interface CopyState {
  isCopied: boolean;
  copyToken: number;
}

type CopyAction = { type: "COPY" } | { type: "RESET"; copyToken: number };

const initialCopyState: CopyState = { isCopied: false, copyToken: 0 };

function copyReducer(state: CopyState, action: CopyAction): CopyState {
  switch (action.type) {
    case "COPY":
      return { isCopied: true, copyToken: state.copyToken + 1 };
    case "RESET":
      return action.copyToken === state.copyToken ? { ...state, isCopied: false } : state;
  }
}

export default function DraftResponseView({
  output,
  showAdjustmentHint = false,
}: DraftResponseViewProps) {
  const intl = useIntl();
  const senderNameFallback = intl.formatMessage({
    id: "app.toolboxChat.draftResponse.senderNameFallback",
  });
  const emailText = formatEmailText(output, senderNameFallback);
  const [state, dispatchCopy] = useReducer(copyReducer, initialCopyState);

  const handleCopy = useCallback(() => {
    navigator.clipboard
      .writeText(emailText)
      .then(() => dispatchCopy({ type: "COPY" }))
      .catch(() => {});
  }, [emailText]);

  useEffect(() => {
    if (!state.isCopied) return;
    const timeoutId = setTimeout(
      () => dispatchCopy({ type: "RESET", copyToken: state.copyToken }),
      6000
    );
    return () => clearTimeout(timeoutId);
  }, [state.isCopied, state.copyToken]);

  return (
    <div className="draft-response-view">
      <p className="draft-response-view__heading">
        {intl.formatMessage({ id: "app.toolboxChat.draftResponse.heading" })}
      </p>

      <div className="draft-response-view__card">
        {output.greeting && (
          <p className="draft-response-view__greeting">{output.greeting}</p>
        )}
        {output.introSummary && (
          <p className="draft-response-view__intro-summary">{output.introSummary}</p>
        )}
        {output.body.map((paragraph, i) => (
          <p key={i} className="draft-response-view__paragraph">
            {paragraph}
          </p>
        ))}
        <div className="draft-response-view__closing">
          {output.closing.closingLine && (
            <p>{output.closing.closingLine}</p>
          )}
          {output.closing.signOff &&
            output.closing.signOff !== output.closing.closingLine && (
              <p>{output.closing.signOff}</p>
            )}
          <p>{output.closing.senderName || senderNameFallback}</p>
        </div>
      </div>

      <p className="draft-response-view__disclaimer">
        {intl.formatMessage({ id: "app.toolboxChat.draftResponse.disclaimer" })}
      </p>

      {showAdjustmentHint && (
        <p className="draft-response-view__adjustment-hint">
          {intl.formatMessage({
            id: "app.toolboxChat.draftResponse.adjustmentHint",
          })}
        </p>
      )}

      <div className="draft-response-view__footer">
        <div className="draft-response-view__copy">
          <WmButton
            buttonType="textonly"
            icon={buildSvgIconHref("copy")}
            iconSize="1.0625rem"
            onClick={handleCopy}
          >
            {intl.formatMessage({ id: "app.toolboxChat.draftResponse.action.copy" })}
          </WmButton>
          {state.isCopied && (
            <span className="draft-response-view__copied" role="status">
              {intl.formatMessage({ id: "app.toolboxChat.draftResponse.action.copied" })}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

interface DraftAdjustFooterProps {
  onAdjust: (adjustment: string) => Promise<boolean>;
  isAdjusting?: boolean;
  adjustError?: string | null;
}

export function DraftAdjustFooter({
  onAdjust,
  isAdjusting = false,
  adjustError = null,
}: DraftAdjustFooterProps) {
  const intl = useIntl();
  const [adjustText, setAdjustText] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = adjustText.trim();
    if (!trimmed || isAdjusting) return;
    const succeeded = await onAdjust(trimmed);
    if (succeeded) setAdjustText("");
    textareaRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e as unknown as React.FormEvent);
    }
  };

  return (
    <div className="draft-adjust-footer">
      <p className="draft-adjust-footer__label" id="draft-adjust-footer-label">
        {intl.formatMessage({ id: "app.toolboxChat.draftResponse.followup" })}
      </p>
      <form className="tcw-input-wrapper" onSubmit={handleSubmit}>
        <textarea
          ref={textareaRef}
          className="tcw-input"
          rows={2}
          placeholder={intl.formatMessage({
            id: "app.toolboxChat.draftResponse.adjust.placeholder",
          })}
          value={adjustText}
          onChange={(e) => setAdjustText(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={isAdjusting}
          aria-labelledby="draft-adjust-footer-label"
        />
        <div className="tcw-send-wrapper">
          <button
            type="submit"
            className="tcw-send-btn"
            disabled={isAdjusting || !adjustText.trim()}
            aria-label={intl.formatMessage({
              id: "app.toolboxChat.draftResponse.adjust.submitAriaLabel",
            })}
          >
            <span className="svg-icon tcw-send-btn-icon"></span>
          </button>
        </div>
      </form>
      {adjustError && <p className="draft-adjust-footer__error">{adjustError}</p>}
      <p className="tcw-disclaimer">
        {intl.formatMessage({ id: "app.toolboxChat.disclaimer" })}
      </p>
    </div>
  );
}
